import { Box, HStack, Icon, Image, Text, VStack, useColorModeValue } from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { FiPaperclip } from 'react-icons/fi';
import { StatusBadge } from '../common/StatusBadge';
import { StarRating } from '../common/StarRating';
import { formatRelativeTime } from '../../utils/formatters';

const GRADIENT_BY_TYPE = {
  attribution: 'linear(to-br, purple.400, blue.500)',
  provenance: 'linear(to-br, blue.400, cyan.500)',
  identification: 'linear(to-br, teal.400, green.500)',
  technical: 'linear(to-br, cyan.400, blue.400)',
  heraldry: 'linear(to-br, orange.400, red.400)',
  archival: 'linear(to-br, yellow.400, orange.400)',
};

export const ClaimCard = ({ claim }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.300');

  const gradient = GRADIENT_BY_TYPE[claim.bountyResearchType] || 'linear(to-br, gray.400, gray.600)';

  return (
    <Box
      as={Link}
      to={`/submissions/${claim.id}`}
      display="block"
      bg={cardBg}
      rounded="xl"
      p={4}
      borderWidth={1}
      borderColor={borderColor}
      _hover={{ borderColor: 'orange.300', boxShadow: 'sm' }}
      transition="all 0.15s"
    >
      <HStack spacing={3} align="start">
        {/* 48×48 bounty thumbnail */}
        <Box w="48px" h="48px" flexShrink={0} rounded="md" overflow="hidden">
          {claim.bountyThumbnailUrl ? (
            <Image src={claim.bountyThumbnailUrl} w="full" h="full" objectFit="cover" alt="" />
          ) : (
            <Box w="full" h="full" bgGradient={gradient} />
          )}
        </Box>

        <VStack align="stretch" spacing={2} flex="1" minW={0}>
          <HStack justify="space-between">
            <StatusBadge status={claim.status} />
            <Text fontSize="xs" color="gray.400" flexShrink={0}>{formatRelativeTime(claim.submittedAt)}</Text>
          </HStack>

          {claim.bountyTitle && (
            <Text fontSize="xs" color="gray.500" fontWeight="medium" noOfLines={1}>
              {claim.bountyTitle}
            </Text>
          )}

          <Text fontSize="sm" color={textColor} noOfLines={2}>
            {claim.researchFindings}
          </Text>

          <HStack justify="space-between" pt={1}>
            <Text fontSize="xs" color="gray.500">
              by {claim.claimantName || claim.claimantAnonymizedUsername}
            </Text>
            <HStack spacing={3}>
              {claim.attachmentUrls?.length > 0 && (
                <HStack spacing={1}>
                  <Icon as={FiPaperclip} boxSize={3} color="gray.400" />
                  <Text fontSize="xs" color="gray.400">{claim.attachmentUrls.length}</Text>
                </HStack>
              )}
              {claim.sources?.length > 0 && (
                <Text fontSize="xs" color="gray.400">
                  {claim.sources.length} source{claim.sources.length !== 1 ? 's' : ''}
                </Text>
              )}
            </HStack>
          </HStack>

          {claim.status === 'approved' && claim.collectorRating && (
            <StarRating rating={claim.collectorRating} size={3} />
          )}
        </VStack>
      </HStack>
    </Box>
  );
};
